import React, { useEffect, useMemo, useState } from 'react';
import { Popup, Toast } from 'antd-mobile';
import axios from 'axios';
import API_ROUTES from '../../../api';

const ROLE_OPTIONS = [
    { value: 0, label: '上场队员', icon: '⚽' },
    { value: 1, label: '主裁', icon: '🟨' },
    { value: 2, label: '边裁', icon: '🚩' },
    { value: 3, label: 'VAR', icon: '📺' },
    { value: 4, label: '观众', icon: '👀' },
];

const SheetHeader = ({ title, subtitle, confirmText, submitting, onClose, onConfirm, danger = false }) => (
    <div className="live-admin-sheet-header">
        <button type="button" className="live-admin-sheet-cancel" onClick={onClose}>取消</button>
        <div className="live-admin-sheet-heading">
            <div className="live-admin-sheet-title">{title}</div>
            <div className="live-admin-sheet-subtitle">{subtitle}</div>
        </div>
        <button
            type="button"
            className={`live-admin-sheet-confirm${danger ? ' danger' : ''}`}
            disabled={submitting}
            onClick={onConfirm}
        >
            {submitting ? '提交中' : confirmText}
        </button>
    </div>
);

const MemberGrid = ({ members, selectedId, getMemberImage, onSelect, danger = false }) => {
    if (!members.length) {
        return <div className="live-admin-sheet-empty">暂无可选择的人员</div>;
    }

    return (
        <div className="live-admin-member-grid">
            {members.map(member => (
                <button
                    key={member.id}
                    type="button"
                    className={`live-admin-member-option${selectedId === member.id ? ' active' : ''}${danger ? ' danger' : ''}`}
                    onClick={() => onSelect(member.id)}
                >
                    <span className="live-admin-member-avatar-wrap">
                        <img src={getMemberImage(member)} alt="" className="live-admin-member-avatar" />
                        {selectedId === member.id && <span className="live-admin-member-check">✓</span>}
                    </span>
                    <span className="live-admin-member-name">{member.name}</span>
                </button>
            ))}
        </div>
    );
};

// “添加临时到场”在同一个底部面板内完成选人、身份和队伍选择，避免多层弹窗。
export const TempArrivalPopup = ({
    visible,
    gameDetail,
    memberListInfo,
    getMemberImage,
    onClose,
    onUpdated,
}) => {
    const [memberId, setMemberId] = useState(null);
    const [role, setRole] = useState(null);
    const [team, setTeam] = useState(1);
    const [submitting, setSubmitting] = useState(false);

    const members = useMemo(() => (
        (memberListInfo || []).filter(item =>
            ![0, 1, 2, 3, 4].includes(item.gameType) && item.tempMember === null
        )
    ), [memberListInfo]);

    useEffect(() => {
        if (!visible) return;
        setMemberId(null);
        setRole(null);
        setTeam(1);
        setSubmitting(false);
    }, [visible]);

    const submit = async () => {
        if (!memberId) {
            Toast.show({ icon: 'fail', content: '请先选择临时到场人员' });
            return;
        }
        if (role === null) {
            Toast.show({ icon: 'fail', content: '请选择报名身份' });
            return;
        }
        if (role === 0 && !team) {
            Toast.show({ icon: 'fail', content: '请选择上场队伍' });
            return;
        }

        const dataPost = {
            gameId: gameDetail.id,
            memberId,
            gameType: role,
            ...(role === 0 ? { gameTeam: team } : {}),
        };

        setSubmitting(true);
        try {
            const response = await axios.post(API_ROUTES.game_tempAddMember, dataPost, {});
            if (response.data.code === 200) {
                Toast.show({ content: '添加成功！', icon: 'success' });
                onClose();
                onUpdated();
            } else {
                Toast.show({ content: response.data.msg, icon: 'fail' });
            }
        } catch (error) {
            Toast.show({ content: '添加失败，请联系管理员！', icon: 'fail' });
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Popup
            visible={visible}
            onMaskClick={onClose}
            bodyStyle={{ borderTopLeftRadius: 24, borderTopRightRadius: 24, maxHeight: '90vh', overflow: 'hidden' }}
            style={{ '--z-index': 1400 }}
        >
            <div className="live-admin-sheet">
                <SheetHeader
                    title="添加临时到场"
                    subtitle="一次完成选人和报名身份"
                    confirmText="确认添加"
                    submitting={submitting}
                    onClose={onClose}
                    onConfirm={submit}
                />
                <div className="live-admin-sheet-content">
                    <div className="live-admin-sheet-section-title"><span>1</span>选择人员</div>
                    <MemberGrid
                        members={members}
                        selectedId={memberId}
                        getMemberImage={getMemberImage}
                        onSelect={setMemberId}
                    />

                    <div className="live-admin-sheet-section-title"><span>2</span>选择报名身份</div>
                    <div className="live-admin-role-grid">
                        {ROLE_OPTIONS.map(item => (
                            <button
                                key={item.value}
                                type="button"
                                className={role === item.value ? 'active' : ''}
                                onClick={() => setRole(item.value)}
                            >
                                <span>{item.icon}</span>
                                <strong>{item.label}</strong>
                                {role === item.value && <em>✓</em>}
                            </button>
                        ))}
                    </div>

                    {role === 0 && (
                        <div className="live-admin-team-section">
                            <div className="live-admin-sheet-section-title"><span>3</span>选择上场队伍</div>
                            <div className="live-admin-team-grid">
                                {[
                                    { value: 1, label: gameDetail?.greenName || '绿队', color: gameDetail?.greenColor || '#607dff' },
                                    { value: 2, label: gameDetail?.redName || '红队', color: gameDetail?.redColor || '#ef5b65' },
                                ].map(item => (
                                    <button
                                        key={item.value}
                                        type="button"
                                        className={team === item.value ? 'active' : ''}
                                        style={{ '--live-team-color': item.color }}
                                        onClick={() => setTeam(item.value)}
                                    >
                                        <i />
                                        <strong>{item.label}</strong>
                                        {team === item.value && <span>✓</span>}
                                    </button>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </Popup>
    );
};

// “标记鸽子”使用独立的单层底部面板，选中人员后只需一次确认。
export const MarkPigeonPopup = ({
    visible,
    gameDetail,
    memberListInfo,
    getMemberImage,
    onClose,
    onUpdated,
}) => {
    const [memberId, setMemberId] = useState(null);
    const [submitting, setSubmitting] = useState(false);

    const members = useMemo(() => (
        (memberListInfo || []).filter(item => [0, 1, 2, 3, 4].includes(item.gameType))
    ), [memberListInfo]);

    useEffect(() => {
        if (!visible) return;
        setMemberId(null);
        setSubmitting(false);
    }, [visible]);

    const selectedMember = members.find(item => item.id === memberId);

    const submit = async () => {
        if (!memberId) {
            Toast.show({ icon: 'fail', content: '请选择需要标记的人员' });
            return;
        }

        const formData = new FormData();
        formData.append('memberId', memberId);
        formData.append('position', 7);
        formData.append('gameId', gameDetail.id);

        setSubmitting(true);
        try {
            const response = await axios.post(API_ROUTES.game_update_bird, formData, {});
            if (response.data.code === 200) {
                Toast.show({ content: '标记成功！', icon: 'success' });
                onClose();
                onUpdated();
            } else {
                Toast.show({ content: response.data.msg, icon: 'fail' });
            }
        } catch (error) {
            Toast.show({ content: '标记失败，请联系管理员！', icon: 'fail' });
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Popup
            visible={visible}
            onMaskClick={onClose}
            bodyStyle={{ borderTopLeftRadius: 24, borderTopRightRadius: 24, maxHeight: '86vh', overflow: 'hidden' }}
            style={{ '--z-index': 1400 }}
        >
            <div className="live-admin-sheet live-admin-sheet-pigeon">
                <SheetHeader
                    title="标记鸽子"
                    subtitle="选择本场未到场的成员"
                    confirmText="确认标记"
                    submitting={submitting}
                    onClose={onClose}
                    onConfirm={submit}
                    danger
                />
                <div className="live-admin-sheet-content">
                    <div className="live-admin-sheet-tip">
                        <span>👋</span>
                        <div>
                            <strong>请选择需要标记的成员</strong>
                            <p>确认后，该成员将从本场到场阵容中移出。</p>
                        </div>
                    </div>
                    <MemberGrid
                        members={members}
                        selectedId={memberId}
                        getMemberImage={getMemberImage}
                        onSelect={setMemberId}
                        danger
                    />
                    {selectedMember && (
                        <div className="live-admin-sheet-summary danger">
                            即将把 <strong>{selectedMember.name}</strong> 标记为鸽子
                        </div>
                    )}
                </div>
            </div>
        </Popup>
    );
};
